import { ChangeDetectionStrategy, Component, computed, EventEmitter, inject, input, Output, signal } from "@angular/core";
import { EstateSaleItemComponent } from "src/app/ev-map/estate-map/estate-sale/estate-sales-list/estate-sale-item.component";
import { EvDropdownComponent, OptionsObject } from "src/app/shared-components/ev-dropdown/ev-dropdown.component";
import { SaleInArea } from "src/models/estate/IResidenceSaleInArea";
import { MarketSalesStateService } from "src/services/estate/market-sales-state.service";

@Component({
    selector: "estate-cluster-popup",
    imports: [EstateSaleItemComponent, EvDropdownComponent],
    templateUrl: "./estate-cluster-popup.component.html",
    styleUrl: "./estate-cluster-popup.component.scss",
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EstateClusterPopupComponent {
    readonly data = input<SaleInArea[]>();
    @Output() closeClicked: EventEmitter<void> = new EventEmitter<void>();

    marketSalesStateService: MarketSalesStateService = inject(MarketSalesStateService);
    showPriceWithCommonDebt: boolean;
    selectedFilter = signal<string>("-sortDate");

    sales = computed(() => [...(this.data() ?? [])].sort(this.dynamicSort(this.selectedFilter())));

    private readonly minMaxPrice = computed(() => this.getMinMaxPrice(this.data() ?? []));
    minPrice = computed(() => this.getMillionPrice(this.minMaxPrice().minPrice));
    maxPrice = computed(() => this.getMillionPrice(this.minMaxPrice().maxPrice));

    headerText = computed(() => {
        const count = this.data()?.length ?? 0;
        return count === 0 ? "" : count > 1 ? "omsetninger" : "omsetning";
    });

    filterOptions = computed(() => {
        return [
            {
                key: "sortDate",
                displayValue: "Dato lav-høy",
            },
            {
                key: "-sortDate",
                displayValue: "Dato høy-lav",
            },
            {
                key: "address",
                displayValue: "Adresse A-Å",
            },
            {
                key: "-address",
                displayValue: "Adresse Å-A",
            },
            {
                key: "residentialMeasuredArea",
                displayValue: "Areal lav-høy",
            },
            {
                key: "-residentialMeasuredArea",
                displayValue: "Areal høy-lav",
            },
            {
                key: "price",
                displayValue: "Pris lav-høy",
            },
            {
                key: "-price",
                displayValue: "Pris høy-lav",
            },
        ];
    });

     onPopupClose(event?: Event): void {
        this.closeClicked.emit();
        event?.stopPropagation();
    }

    setFilter(selected: OptionsObject) {
        this.selectedFilter.set(selected.key as string);
    }

        dynamicSort(property: string): (a: any, b: any) => number {
        let sortOrder = 1;
        if (property[0] === "-") {
            sortOrder = -1;
            property = property.substr(1);
        }
        return function (a, b) {
            const result = a[property] < b[property] ? -1 : a[property] > b[property] ? 1 : 0;
            return result * sortOrder;
        };
    }

    private getMinMaxPrice(sales: SaleInArea[]): { maxPrice: number; minPrice: number } {
        let max: number = null;
        let min: number = null;

        sales.forEach((estate: SaleInArea) => {
            const price: number = this.getPriceFromEstate(estate);
            if (max === null || price > max) {
                max = price;
            }
            if (min === null || price < min) {
                min = price;
            }
        });
        return { maxPrice: max, minPrice: min };
    }

    private getPriceFromEstate(estate: SaleInArea): number {
        return estate.price;
    }

    private getMillionPrice(price: number): string {
        if (isNaN(price)) {
            return "";
        }
        if (price < 100000) {
            return "0,1";
        }

        const dotPrice: string = (price / 1000000).toFixed(1);
        return dotPrice.replace(/[.]/g, ",");
    }
}
